import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Briefcase, Plus, Sparkles, Pencil, Trash2 } from "lucide-react";
import { fmtPct, fmtINR } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

const VARIANT_INFO = {
    STANDARD: { label: "Standard", tone: "slate", note: "Overhead + Profit on direct material+conversion cost. Most common." },
    KOBELCO_CE: { label: "Kobelco CE", tone: "blue", note: "Overhead applied ONLY on process cost — not on material total." },
    VOLVO_EXPORT: { label: "Volvo Export", tone: "emerald", note: "Deducts 4% (discount) from grand total at the very end." },
    FES_LYNX: { label: "FES Lynx", tone: "amber", note: "Different profit % for cowl-shipped vs kit-shipped parts." },
    JCB_DOMESTIC: { label: "JCB Domestic", tone: "copper", note: "Standard structure with JCB-specific overhead loading." },
    CUSTOM: { label: "Custom", tone: "violet", note: "Configure overhead/profit basis below — no code change needed to onboard a new OEM's commercial structure." },
};

const EMPTY_FORM = {
    code: "", name: "", industry: "CV", contact_person: "",
    formula_variant: "STANDARD", is_active: true,
    icc_pct: 2.5, overhead_pct: 8.0, profit_pct: 12.0, discount_pct: 1.5,
    overhead_fixed: "", profit_fixed: "",
    // Volvo split-basis extras
    rejection_pct: "", volume_rebate_rs: "", rm_rate_adjustment: "",
    rm_rate_adjustment_basis: "", freight_scope: "SURIN_ARRANGES",
    // CUSTOM variant — configurable basis rules, no code change to onboard
    overhead_basis: "TOTAL_COST", profit_basis: "TOTAL",
    profit_rm_pct: "", profit_process_pct: "",
};

export default function Customers() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // customer being edited (null = add mode)
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/customers").then((r) => setRows(r.data)).catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const openAdd = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setOpen(true);
    };

    const openEdit = (c) => {
        const n = c.current_norm || {};
        setEditing(c);
        setForm({
            code: c.code || "",
            name: c.name || "",
            industry: c.industry || "CV",
            contact_person: c.contact_person || "",
            formula_variant: c.formula_variant || "STANDARD",
            is_active: c.is_active !== false,
            icc_pct: n.icc_pct ?? 0,
            overhead_pct: n.overhead_pct ?? 0,
            profit_pct: n.profit_pct ?? 0,
            discount_pct: n.discount_pct ?? 0,
            overhead_fixed: n.overhead_fixed ?? "",
            profit_fixed: n.profit_fixed ?? "",
            rejection_pct: n.rejection_pct ?? "",
            volume_rebate_rs: n.volume_rebate_rs ?? "",
            rm_rate_adjustment: n.rm_rate_adjustment ?? "",
            rm_rate_adjustment_basis: n.rm_rate_adjustment_basis || "",
            freight_scope: n.freight_scope || "SURIN_ARRANGES",
            overhead_basis: n.overhead_basis || "TOTAL_COST",
            profit_basis: n.profit_basis || "TOTAL",
            profit_rm_pct: n.profit_rm_pct ?? "",
            profit_process_pct: n.profit_process_pct ?? "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        setSubmitting(true);
        try {
            const isVolvo = form.formula_variant === "VOLVO_EXPORT";
            const isCustom = form.formula_variant === "CUSTOM";
            const normPayload = {
                icc_pct: parseFloat(form.icc_pct) || 0,
                overhead_pct: parseFloat(form.overhead_pct) || 0,
                profit_pct: parseFloat(form.profit_pct) || 0,
                discount_pct: parseFloat(form.discount_pct) || 0,
                overhead_fixed: form.overhead_fixed === "" ? null : parseFloat(form.overhead_fixed),
                profit_fixed: form.profit_fixed === "" ? null : parseFloat(form.profit_fixed),
                ...(isVolvo ? {
                    rejection_pct: form.rejection_pct === "" ? 0 : parseFloat(form.rejection_pct),
                    volume_rebate_rs: form.volume_rebate_rs === "" ? 0 : parseFloat(form.volume_rebate_rs),
                    rm_rate_adjustment: form.rm_rate_adjustment === "" ? 0 : parseFloat(form.rm_rate_adjustment),
                    rm_rate_adjustment_basis: form.rm_rate_adjustment_basis || "",
                    freight_scope: form.freight_scope || "SURIN_ARRANGES",
                } : {}),
                ...(isCustom ? {
                    overhead_basis: form.overhead_basis || "TOTAL_COST",
                    profit_basis: form.profit_basis || "TOTAL",
                    profit_rm_pct: form.profit_rm_pct === "" ? null : parseFloat(form.profit_rm_pct),
                    profit_process_pct: form.profit_process_pct === "" ? null : parseFloat(form.profit_process_pct),
                } : {}),
            };

            if (editing) {
                // PROFILE update (code is locked once created)
                await api.put(`/customers/${editing.id}`, {
                    name: form.name,
                    industry: form.industry,
                    contact_person: form.contact_person,
                    formula_variant: form.formula_variant,
                    is_active: form.is_active,
                });
                // NORM update — creates a new versioned norm row server-side
                await api.post(`/customers/${editing.id}/norm`, normPayload);
                toast.success(`${editing.code} updated — new norm version saved`);
            } else {
                await api.post("/customers", {
                    code: form.code, name: form.name, industry: form.industry,
                    contact_person: form.contact_person, formula_variant: form.formula_variant,
                    is_active: form.is_active, ...normPayload,
                });
                toast.success("Customer added");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete customer ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/customers/${row.id}`);
            toast.success("Customer deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const isVolvo = form.formula_variant === "VOLVO_EXPORT";

    return (
        <div data-testid={TID.customersPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 6"
                title="Customers & Norms"
                subtitle="OEM customers with their commercial norms and pricing formula variant. When an RFQ is created, these load automatically; never typed manually."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="customers" label="Customers" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openAdd} data-testid={TID.customersAddBtn}>
                                <Plus className="h-4 w-4" /> Add Customer
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {rows && rows.length === 0 && <EmptyState icon={Briefcase} title="No customers yet" />}

            {rows && rows.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                        <table data-testid={TID.customersTable} className="w-full table-rowhover">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="eyebrow text-left py-2 px-4">Code</th>
                                    <th className="eyebrow text-left py-2 px-4">Customer</th>
                                    <th className="eyebrow text-left py-2 px-4">Industry</th>
                                    <th className="eyebrow text-left py-2 px-4">Contact</th>
                                    <th className="eyebrow text-left py-2 px-4">Formula</th>
                                    <th className="eyebrow text-right py-2 px-4">ICC %</th>
                                    <th className="eyebrow text-right py-2 px-4">OH %</th>
                                    <th className="eyebrow text-right py-2 px-4">Profit %</th>
                                    <th className="eyebrow text-right py-2 px-4">Disc %</th>
                                    {canEdit && <th className="eyebrow text-right py-2 px-4 w-28"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => {
                                    const info = VARIANT_INFO[c.formula_variant] || {};
                                    const norm = c.current_norm;
                                    const isVECV = !!(norm?.overhead_fixed || norm?.profit_fixed);
                                    return (
                                        <tr key={c.id} className="border-b border-slate-100">
                                            <td className="py-1.5 px-4 text-[13px] font-mono font-semibold">{c.code}</td>
                                            <td className="py-1.5 px-4 text-[13px] font-medium">{c.name}</td>
                                            <td className="py-1.5 px-4"><Pill tone="slate">{c.industry}</Pill></td>
                                            <td className="py-1.5 px-4 text-[12px] text-slate-600">{c.contact_person}</td>
                                            <td className="py-1.5 px-4">
                                                <Pill tone={info.tone || "slate"} className="!normal-case tracking-normal">
                                                    <Sparkles className="h-2.5 w-2.5" /> {info.label || c.formula_variant}
                                                </Pill>
                                            </td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">{fmtPct(norm?.icc_pct ?? 0)}</td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">
                                                {isVECV ? <span className="text-[hsl(var(--copper))]" title="VECV fixed ₹">{fmtINR(norm.overhead_fixed, 0)}</span> : fmtPct(norm?.overhead_pct ?? 0)}
                                            </td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">
                                                {isVECV ? <span className="text-[hsl(var(--copper))]" title="VECV fixed ₹">{fmtINR(norm.profit_fixed, 0)}</span> : fmtPct(norm?.profit_pct ?? 0)}
                                            </td>
                                            <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px]">{fmtPct(norm?.discount_pct ?? 0)}</td>
                                            {canEdit && (
                                                <td className="py-1.5 px-4 text-right">
                                                    <div className="inline-flex gap-1">
                                                        <button
                                                            onClick={() => openEdit(c)}
                                                            className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:bg-slate-50 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors text-slate-600"
                                                            data-testid={`customer-edit-${c.code}`}
                                                            title={`Edit ${c.code} profile + norms`}
                                                        >
                                                            <Pencil className="w-3 h-3" /> Edit
                                                        </button>
                                                        <button
                                                            onClick={() => remove(c)}
                                                            className="inline-flex items-center gap-1 h-7 px-2 text-[11px] rounded-md border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors text-slate-600"
                                                            data-testid={`customer-delete-${c.code}`}
                                                            title={`Delete ${c.code}`}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-4 border-t border-slate-100 bg-slate-50/40">
                        <div className="eyebrow text-slate-500 mb-2">FORMULA VARIANT CHEAT SHEET</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2.5">
                            {Object.entries(VARIANT_INFO).map(([k, v]) => (
                                <div key={k} className="rounded-md border border-slate-200 bg-white p-2.5">
                                    <Pill tone={v.tone} className="mb-1.5">{v.label}</Pill>
                                    <div className="text-[11px] text-slate-600 leading-snug">{v.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={editing ? `Edit · ${editing.code}` : "Add Customer"}
                size="xl"
                footer={<>
                    <SlateButton variant="outline" onClick={() => setOpen(false)}>Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={submitting} data-testid="customer-save-btn">
                        {submitting ? "Saving…" : editing ? "Save Changes" : "Create Customer"}
                    </CopperButton>
                </>}
            >
                <form onSubmit={submit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field
                            label="Customer Code"
                            required
                            helper={editing ? "Code is locked — referenced across RFQs and Settlements." : "e.g., TATA-MOTORS"}
                        >
                            <TextInput
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                required
                                disabled={!!editing}
                                data-testid="customer-form-code"
                            />
                        </Field>
                        <Field label="Industry" required>
                            <Select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
                                <option value="CV">Commercial Vehicle</option>
                                <option value="CE">Construction Equipment</option>
                                <option value="FARM">Farm Equipment</option>
                                <option value="EXPORT">Export</option>
                                <option value="AUTO">Auto</option>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Customer Name" required>
                        <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Primary Contact" required>
                            <TextInput value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} required />
                        </Field>
                        <Field label="Pricing Formula Variant" required>
                            <Select value={form.formula_variant} onChange={(e) => setForm({ ...form, formula_variant: e.target.value })}>
                                {Object.entries(VARIANT_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </Select>
                        </Field>
                    </div>
                    {editing && (
                        <Field label="Active">
                            <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="h-4 w-4 rounded border-slate-300 accent-[hsl(var(--copper))]"
                                />
                                {form.is_active ? "Customer is active" : "Customer is inactive (hidden from new RFQs)"}
                            </label>
                        </Field>
                    )}

                    <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                        <div className="eyebrow text-slate-500 mb-2">
                            COMMERCIAL NORMS {editing ? "· will save as a new versioned norm" : "(current period)"}
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                            <Field label="ICC %"><NumberInput value={form.icc_pct} onChange={(e) => setForm({ ...form, icc_pct: e.target.value })} step="0.01" /></Field>
                            <Field label="Overhead %"><NumberInput value={form.overhead_pct} onChange={(e) => setForm({ ...form, overhead_pct: e.target.value })} step="0.01" /></Field>
                            <Field label="Profit %"><NumberInput value={form.profit_pct} onChange={(e) => setForm({ ...form, profit_pct: e.target.value })} step="0.01" /></Field>
                            <Field label="Discount %"><NumberInput value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} step="0.01" /></Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <Field label="Overhead (fixed ₹)" helper="VECV exception · Rule 10">
                                <NumberInput value={form.overhead_fixed} onChange={(e) => setForm({ ...form, overhead_fixed: e.target.value })} step="1" placeholder="leave blank if %" />
                            </Field>
                            <Field label="Profit (fixed ₹)" helper="VECV exception · Rule 10">
                                <NumberInput value={form.profit_fixed} onChange={(e) => setForm({ ...form, profit_fixed: e.target.value })} step="1" placeholder="leave blank if %" />
                            </Field>
                        </div>

                        {isVolvo && (
                            <div className="mt-4 pt-3 border-t border-slate-200">
                                <div className="eyebrow text-[hsl(var(--copper))] mb-2">VOLVO SPLIT-BASIS EXTRAS</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Rejection %" helper="on notional base">
                                        <NumberInput value={form.rejection_pct} onChange={(e) => setForm({ ...form, rejection_pct: e.target.value })} step="0.0001" placeholder="0" />
                                    </Field>
                                    <Field label="Volume Rebate (₹)" helper="fixed, deducted post-discount">
                                        <NumberInput value={form.volume_rebate_rs} onChange={(e) => setForm({ ...form, volume_rebate_rs: e.target.value })} step="0.01" placeholder="0" />
                                    </Field>
                                    <Field label="RM Rate Adjustment (₹)" helper="-(rm_new − rm_old) × profit_pct, pre-calculated">
                                        <NumberInput value={form.rm_rate_adjustment} onChange={(e) => setForm({ ...form, rm_rate_adjustment: e.target.value })} step="0.01" placeholder="0" />
                                    </Field>
                                    <Field label="Freight Scope">
                                        <Select value={form.freight_scope} onChange={(e) => setForm({ ...form, freight_scope: e.target.value })}>
                                            <option value="SURIN_ARRANGES">ABC COMPANY arranges (TPT applies)</option>
                                            <option value="CUSTOMER_ARRANGES">Customer arranges (TPT = 0)</option>
                                        </Select>
                                    </Field>
                                </div>
                                <Field label="RM Rate Adjustment Note" helper="free-text description of the adjustment basis">
                                    <TextInput value={form.rm_rate_adjustment_basis} onChange={(e) => setForm({ ...form, rm_rate_adjustment_basis: e.target.value })} placeholder="e.g., Reconciliation against 2018 RM rate" />
                                </Field>
                            </div>
                        )}

                        {form.formula_variant === "CUSTOM" && (
                            <div className="mt-4 pt-3 border-t border-slate-200">
                                <div className="eyebrow text-violet-700 mb-2">CUSTOM FORMULA BASIS</div>
                                <p className="text-[11px] text-slate-500 mb-2">
                                    Onboard a new OEM's commercial structure without a code change — pick how Overhead and Profit are based, using the % fields above (and Profit RM/Process % below if Split).
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Overhead Basis">
                                        <Select value={form.overhead_basis} onChange={(e) => setForm({ ...form, overhead_basis: e.target.value })}>
                                            <option value="TOTAL_COST">Total Cost (material + process)</option>
                                            <option value="PROCESS_COST_ONLY">Process Cost Only</option>
                                        </Select>
                                    </Field>
                                    <Field label="Profit Basis">
                                        <Select value={form.profit_basis} onChange={(e) => setForm({ ...form, profit_basis: e.target.value })}>
                                            <option value="TOTAL">Flat % on Cost + Additions</option>
                                            <option value="SPLIT">Split — different % on RM vs Process</option>
                                        </Select>
                                    </Field>
                                </div>
                                {form.profit_basis === "SPLIT" && (
                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <Field label="Profit % on RM+BOP" helper="falls back to Profit % above if blank">
                                            <NumberInput value={form.profit_rm_pct} onChange={(e) => setForm({ ...form, profit_rm_pct: e.target.value })} step="0.01" placeholder="e.g. 8" />
                                        </Field>
                                        <Field label="Profit % on Process" helper="falls back to Profit % above if blank">
                                            <NumberInput value={form.profit_process_pct} onChange={(e) => setForm({ ...form, profit_process_pct: e.target.value })} step="0.01" placeholder="e.g. 15" />
                                        </Field>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </div>
    );
}